// src/pages/step/components/MergeStepPathModal.jsx
import { useState } from "react";
import { Layers, Loader2, X, RefreshCw, Eye } from "lucide-react";
import { stepSkillsAPI } from "../../../services/stepService";

export default function MergeStepPathModal({ candidates, target, onClose, onDone }) {
  const [selected, setSelected] = useState(() => candidates.map((s) => s.id));
  const [title, setTitle] = useState("");
  const [deactivate, setDeactivate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(null); // "preview" | "merge"
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);

  const toggle = (id) => {
    setPreview(null);
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const run = async (dryRun) => {
    if (selected.length === 0) {
      setError("اختار مهارة واحدة على الأقل");
      return;
    }
    try {
      setLoading(true);
      setMode(dryRun ? "preview" : "merge");
      setError("");
      const payload = {
        source_skill_ids: selected,
        deactivate_sources: deactivate,
        dry_run: dryRun,
      };
      if (target) payload.target_skill_id = target.id;
      else if (title.trim()) payload.title = title.trim();

      const data = await stepSkillsAPI.mergePath(payload);
      if (dryRun) setPreview(data);
      else onDone(data);
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          "حصل خطأ أثناء الدمج"
      );
    } finally {
      setLoading(false);
      setMode(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-gray-900">
              {target ? "تحديث المهارة المجمّعة" : "Merge General Path"}
            </h3>
            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center">
              {target ? (
                <RefreshCw className="w-5 h-5 text-blue-700" />
              ) : (
                <Layers className="w-5 h-5 text-blue-700" />
              )}
            </div>
          </div>
        </div>

        <p className="text-sm text-gray-500 text-right mb-4">
          {target
            ? `هيتم نسخ العناصر الجديدة بس إلى "${target.title}".`
            : "هيتم نسخ محتوى المهارات المختارة داخل مهارة General Path واحدة جديدة."}
        </p>

        {!target && (
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="عنوان المهارة الجديدة (اختياري)"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-right mb-4 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
        )}

        <div className="flex items-center justify-between mb-2">
          <div className="flex gap-3 text-xs">
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setSelected(candidates.map((s) => s.id));
              }}
              className="text-blue-600 hover:underline"
            >
              تحديد الكل
            </button>
            <button
              type="button"
              onClick={() => {
                setPreview(null);
                setSelected([]);
              }}
              className="text-gray-500 hover:underline"
            >
              إلغاء الكل
            </button>
          </div>
          <span className="text-sm font-medium text-gray-700">
            المهارات المصدر ({selected.length}/{candidates.length})
          </span>
        </div>

        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-56 overflow-y-auto mb-4">
          {candidates.map((s) => (
            <label
              key={s.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50"
            >
              <span className="text-xs text-gray-400">
                {s.total_questions || 0} Questions
              </span>
              <span className="flex items-center gap-2 text-sm text-gray-800">
                {s.title}
                {!s.is_active && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                    Inactive
                  </span>
                )}
                <input
                  type="checkbox"
                  checked={selected.includes(s.id)}
                  onChange={() => toggle(s.id)}
                  className="w-4 h-4 accent-blue-600"
                />
              </span>
            </label>
          ))}
        </div>

        <label className="flex items-center justify-end gap-2 text-sm text-gray-700 mb-4 cursor-pointer">
          تعطيل المهارات الأصلية بعد النسخ
          <input
            type="checkbox"
            checked={deactivate}
            onChange={(e) => setDeactivate(e.target.checked)}
            className="w-4 h-4 accent-blue-600"
          />
        </label>

        {preview && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-sm text-right">
            <p className="font-semibold text-blue-800 mb-2">
              معاينة: هيتم نسخ {preview.total_copied} عنصر
            </p>
            <ul className="space-y-1 text-blue-900">
              {(preview.sources || []).map((r) => (
                <li key={r.skill_id} className="flex justify-between gap-2">
                  <span className="text-xs">
                    نسخ {r.copied} · تخطي {r.skipped}
                  </span>
                  <span>{r.title}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm text-right mb-4">
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-60"
          >
            إلغاء
          </button>
          <button
            onClick={() => run(true)}
            disabled={loading}
            className="flex-1 py-2.5 border border-blue-200 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && mode === "preview" ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            معاينة
          </button>
          <button
            onClick={() => run(false)}
            disabled={loading}
            className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading && mode === "merge" && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {target ? "تحديث" : "دمج"}
          </button>
        </div>
      </div>
    </div>
  );
}
